import React from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import {
  Button,
  Checkbox,
  Divider,
  Fieldset,
  Grid,
  GridCell,
  Label,
  Select,
  SelectOption,
  Text,
} from "@flixbus/honeycomb-react";
import { Icon, IconPlus } from "@flixbus/honeycomb-icons-react";
import type { CalculableCategory } from "../../../../hooks/useCreateCalculationPanel";
import type { CreateCalculationFormData } from "../../CreateCalculationPanel.constants";
import RemoveCategoryButton from "./RemoveCategoryButton.component";
import styles from "./Step2Subcategory.module.scss";

interface Step2SubcategoryProps {
  form: UseFormReturn<CreateCalculationFormData>;
  calculableCategories: CalculableCategory[];
  sections: string[];
  onSectionsChange: (sections: string[]) => void;
}

const UNSELECTED = "";

const Step2Subcategory: React.FC<Step2SubcategoryProps> = ({
  form,
  calculableCategories,
  sections,
  onSectionsChange,
}) => {
  const subIdsOf = (categoryId: string) =>
    calculableCategories
      .find((c) => c.id === categoryId)
      ?.subCategories.map((s) => s.id) ?? [];

  return (
    <Controller
      name="subCategoryIds"
      control={form.control}
      render={({ field }) => {
        const selected = field.value;
        const withoutCategory = (categoryId: string) =>
          selected.filter((id) => !subIdsOf(categoryId).includes(id));

        const setCategoryAt = (index: number, categoryId: string) => {
          field.onChange(withoutCategory(sections[index]));
          onSectionsChange(
            sections.map((c, i) => (i === index ? categoryId : c)),
          );
        };

        const removeSection = (index: number) => {
          field.onChange(withoutCategory(sections[index]));
          onSectionsChange(sections.filter((_, i) => i !== index));
        };

        const toggleSub = (id: string, checked: boolean) =>
          field.onChange(
            checked ? [...selected, id] : selected.filter((v) => v !== id),
          );

        const takenCategories = new Set(sections.filter(Boolean));

        return (
          <>
            {sections.map((categoryId, index) => {
              const category = calculableCategories.find(
                (c) => c.id === categoryId,
              );
              return (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <div data-testid={`category-section-${index}`}>
                    <Label
                      text="Category*"
                      labelFor={`create-category-${index}`}
                    />
                    <Grid align="start" extraClasses={styles.categoryGrid}>
                      <GridCell>
                        <Select
                          id={`create-category-${index}`}
                          aria-label="Category"
                          placeholder="— Select —"
                          value={categoryId}
                          onChange={(e) => setCategoryAt(index, e.target.value)}
                        >
                          <SelectOption value={UNSELECTED}>
                            {"— Select —"}
                          </SelectOption>
                          {calculableCategories.map((cat) => (
                            <SelectOption
                              key={cat.id}
                              value={cat.id}
                              disabled={
                                cat.id !== categoryId &&
                                takenCategories.has(cat.id)
                              }
                            >
                              {cat.name}
                            </SelectOption>
                          ))}
                        </Select>
                      </GridCell>
                      {sections.length > 1 && (
                        <GridCell alignSelf="center">
                          <RemoveCategoryButton
                            index={index}
                            onRemove={removeSection}
                          />
                        </GridCell>
                      )}
                    </Grid>

                    {category && (
                      <div className={styles.subcategories}>
                        <Fieldset
                          legend={`Select ${category.name} subcategories`}
                          itemSpacing="2"
                        >
                          {category.subCategories.map((sub) => (
                            <Checkbox
                              key={sub.id}
                              id={`create-subcategory-${sub.id}`}
                              label={sub.name}
                              value={sub.id}
                              checked={selected.includes(sub.id)}
                              onChange={(e) =>
                                toggleSub(sub.id, e.target.checked)
                              }
                            />
                          ))}
                        </Fieldset>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            <div>
              <Button
                appearance="link"
                disabled={takenCategories.size >= calculableCategories.length}
                onClick={() => onSectionsChange([...sections, UNSELECTED])}
                extraClasses={styles.addButton}
              >
                <span aria-hidden="true">
                  <Icon InlineIcon={IconPlus} aria-hidden="true" />
                </span>
                {" Add category"}
              </Button>
            </div>

            <Text small>
              Tip: Select at least one category with one subcategory. Use “Add
              category” to include subcategories from more than one category.
            </Text>
          </>
        );
      }}
    />
  );
};

export default React.memo(Step2Subcategory);
